import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Plus, Trash2, UserCog, Pencil, Eye, History } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const ROLE_LABELS = { admin: "Yönetici", data_entry: "Veri Giriş", viewer: "Görüntüleyici" };
const ROLE_STYLES = {
  admin: "bg-blue-50 text-[#1E3A8A] border-blue-200",
  data_entry: "bg-slate-100 text-slate-700 border-slate-200",
  viewer: "bg-amber-50 text-amber-800 border-amber-200",
};
const emptyForm = { username: "", name: "", password: "", email: "", phone: "", role: "data_entry" };

export default function Users() {
  const { user: me, refreshMe } = useAuth();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null); // user object or null for new
  const [form, setForm] = useState(emptyForm);
  const [audit, setAudit] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);

  // SADECE username=="admin" (ana yönetici) "Gözünden Bak" ve audit görebilir.
  const isSuperAdmin = me?.username === "admin" && !me?.view_as;

  const load = async () => setItems((await api.get("/users")).data);
  const loadAudit = async () => {
    if (!isSuperAdmin) return;
    setAuditLoading(true);
    try {
      const { data } = await api.get("/admin/view-as/audit");
      setAudit(data.records || []);
    } catch { /* silent */ }
    finally { setAuditLoading(false); }
  };
  useEffect(() => { load(); loadAudit(); /* eslint-disable-next-line */ }, [me?.id]);

  const viewAs = async (u) => {
    if (!isSuperAdmin) return;
    if (u.username === "admin") return toast.error("Ana yönetici hesabı görüntülenemez");
    try {
      await api.post(`/admin/view-as/${u.id}`);
      toast.success(`${u.name} olarak görüntülüyorsunuz`);
      await refreshMe();
      // Ana sayfaya dönerek hedef kullanıcının menülerinin yeniden yüklenmesini sağla
      window.location.href = "/dashboard";
    } catch (e) {
      toast.error(e.response?.data?.detail || "Görüntüleme başlatılamadı");
    }
  };

  const openNew = () => {
    setEditing(null);
    setForm(emptyForm);
    setOpen(true);
  };
  const openEdit = (u) => {
    setEditing(u);
    setForm({ username: u.username, name: u.name, password: "", email: u.email || "", phone: u.phone || "", role: u.role });
    setOpen(true);
  };

  const save = async () => {
    if (editing) {
      const payload = {};
      if (form.name && form.name !== editing.name) payload.name = form.name;
      if (form.email !== (editing.email || "")) payload.email = form.email;
      if (form.phone !== (editing.phone || "")) payload.phone = form.phone;
      if (form.role !== editing.role) payload.role = form.role;
      if (form.password) payload.password = form.password;
      if (Object.keys(payload).length === 0) return toast.info("Değişiklik yok");
      try {
        await api.patch(`/users/${editing.id}`, payload);
        toast.success("Kullanıcı güncellendi");
        setOpen(false);
        load();
      } catch (e) {
        toast.error(e.response?.data?.detail || "Hata");
      }
      return;
    }
    if (!form.username || !form.password || !form.name) return toast.error("Kullanıcı adı, ad ve şifre zorunlu");
    try {
      await api.post("/users", form);
      toast.success("Kullanıcı eklendi");
      setOpen(false);
      setForm(emptyForm);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Hata");
    }
  };

  const del = async (id) => {
    if (!window.confirm("Silmek istediğinize emin misiniz?")) return;
    await api.delete(`/users/${id}`);
    toast.success("Silindi"); load();
  };

  return (
    <div className="space-y-6" data-testid="users-page">
      <div className="flex items-baseline justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-slate-500 font-mono">Erişim Yönetimi</div>
          <h1 className="mt-1 font-heading font-bold text-3xl lg:text-4xl tracking-tight text-slate-900">Kullanıcılar</h1>
          <p className="mt-2 text-sm text-slate-500">
            Kullanıcı adı ile giriş yapılır. E-posta yalnızca bildirim almak isteyenler için opsiyoneldir.
          </p>
        </div>
        <Button onClick={openNew} data-testid="add-user-btn" className="bg-[#1E3A8A] hover:bg-[#1e40af]">
          <Plus className="w-4 h-4 mr-2" /> Yeni Kullanıcı
        </Button>
      </div>

      <Card className="border-slate-200 shadow-none">
        <CardHeader className="border-b border-slate-100"><CardTitle className="font-heading text-lg">Sistem Kullanıcıları</CardTitle></CardHeader>
        <CardContent className="pt-6">
          <div className="overflow-x-auto rounded-md border border-slate-200">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50 hover:bg-slate-50">
                  <TableHead>Ad</TableHead>
                  <TableHead>Kullanıcı Adı</TableHead>
                  <TableHead>Cep (SMS OTP)</TableHead>
                  <TableHead>E-posta (Bildirim)</TableHead>
                  <TableHead>Rol</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((u) => (
                  <TableRow key={u.id} className="hover:bg-slate-50" data-testid={`user-row-${u.username}`}>
                    <TableCell className="font-semibold flex items-center gap-2">
                      <div className="w-8 h-8 rounded-md bg-blue-50 text-[#1E3A8A] flex items-center justify-center"><UserCog className="w-4 h-4" /></div>
                      {u.name}
                    </TableCell>
                    <TableCell className="font-mono text-xs">@{u.username}</TableCell>
                    <TableCell className="font-mono text-xs">
                      {u.phone ? <span className="text-emerald-700">+{u.phone}</span> : <span className="text-rose-500">— SMS yok</span>}
                    </TableCell>
                    <TableCell className="font-mono text-xs text-slate-500">{u.email || "—"}</TableCell>
                    <TableCell>
                      <span className={`text-xs px-2 py-1 rounded border ${ROLE_STYLES[u.role] || ROLE_STYLES.viewer}`}>
                        {ROLE_LABELS[u.role] || u.role}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      {isSuperAdmin && u.username !== "admin" && !u.deleted && (
                        <Button size="sm" variant="ghost" onClick={() => viewAs(u)} data-testid={`view-as-${u.username}`} title="Bu kullanıcının gözünden bak">
                          <Eye className="w-4 h-4 text-[#1E3A8A]" />
                        </Button>
                      )}
                      <Button size="sm" variant="ghost" onClick={() => openEdit(u)} data-testid={`edit-user-${u.username}`}>
                        <Pencil className="w-4 h-4 text-slate-500" />
                      </Button>
                      {u.id !== me?.id && (
                        <Button size="sm" variant="ghost" onClick={() => del(u.id)} data-testid={`delete-user-${u.username}`}>
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Update 29.4 — Görüntüleme geçmişi (SADECE ana yönetici) */}
      {isSuperAdmin && (
        <Card className="border-slate-200 shadow-none" data-testid="view-as-history-card">
          <CardHeader className="border-b border-slate-100">
            <CardTitle className="font-heading text-lg flex items-center gap-2">
              <History className="w-4 h-4 text-[#1E3A8A]" /> Görüntüleme Geçmişi
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-6">
            {auditLoading ? (
              <div className="text-xs text-slate-500">Yükleniyor…</div>
            ) : audit.length === 0 ? (
              <div className="text-xs text-slate-500">Kayıt yok.</div>
            ) : (
              <div className="overflow-x-auto rounded-md border border-slate-200">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50 hover:bg-slate-50">
                      <TableHead>Hedef</TableHead>
                      <TableHead>Rol</TableHead>
                      <TableHead>Başlangıç</TableHead>
                      <TableHead>Bitiş</TableHead>
                      <TableHead>Sebep</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {audit.map((r) => (
                      <TableRow key={r.id} data-testid={`va-audit-${r.id}`}>
                        <TableCell className="text-xs">
                          <div className="font-semibold">{r.target_name}</div>
                          <div className="text-slate-500 font-mono">@{r.target_username}</div>
                        </TableCell>
                        <TableCell className="text-xs font-mono">{r.target_role}</TableCell>
                        <TableCell className="text-xs font-mono">{(r.started_at || "").slice(0, 19).replace("T", " ")}</TableCell>
                        <TableCell className="text-xs font-mono">
                          {r.ended_at ? r.ended_at.slice(0, 19).replace("T", " ") : <span className="text-emerald-700">— aktif —</span>}
                        </TableCell>
                        <TableCell className="text-xs">
                          {r.end_reason === "manual_stop" ? "Manuel" : r.end_reason === "auto_expired" ? "Süre doldu" : "—"}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-heading">{editing ? `Kullanıcıyı Düzenle (@${editing.username})` : "Yeni Kullanıcı"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            {!editing && (
              <div>
                <Label className="text-xs uppercase tracking-wider">Kullanıcı Adı *</Label>
                <Input
                  value={form.username}
                  onChange={(e) => setForm({ ...form, username: e.target.value.toLowerCase() })}
                  placeholder="ör: usta1, atelye_sef"
                  className="mt-1 font-mono"
                  data-testid="user-username-input"
                  autoCapitalize="none"
                />
                <div className="text-[10px] text-slate-500 mt-1">Küçük harfe dönüştürülür. Serbest karakterler kullanılabilir.</div>
              </div>
            )}
            <div><Label className="text-xs uppercase tracking-wider">Ad Soyad *</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1" data-testid="user-name-input" />
            </div>
            <div><Label className="text-xs uppercase tracking-wider">Cep Telefonu (SMS OTP için) *</Label>
              <Input
                type="tel"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                className="mt-1 font-mono"
                placeholder="05xx xxx xxxx"
                data-testid="user-phone-input"
                inputMode="tel"
              />
              <div className="text-[10px] text-slate-500 mt-1">Bu numaraya SMS OTP gönderilir. Telefon girilmezse kullanıcı giriş yapamaz (yalnızca admin acil kanaldan girer).</div>
            </div>
            <div><Label className="text-xs uppercase tracking-wider">E-posta (Opsiyonel — bildirim için)</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1" placeholder="istege bagli" data-testid="user-email-input" />
            </div>
            <div><Label className="text-xs uppercase tracking-wider">{editing ? "Yeni Şifre (Boş bırakılırsa değişmez)" : "Şifre *"}</Label>
              <Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-1" data-testid="user-password-input" />
            </div>
            <div><Label className="text-xs uppercase tracking-wider">Rol</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger className="mt-1" data-testid="user-role-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="viewer">Görüntüleyici (Sadece Okuma)</SelectItem>
                  <SelectItem value="data_entry">Veri Giriş Personeli</SelectItem>
                  <SelectItem value="admin">Yönetici</SelectItem>
                </SelectContent>
              </Select>
              <div className="text-[10px] text-slate-500 mt-1 leading-relaxed">
                <b>Görüntüleyici</b> yalnızca raporları görüntüler, iş emri giremez.
                <br /><b>Veri Giriş</b> iş emri girer.
                <b> Yönetici</b> tam yetkilidir.
              </div>
            </div>
            <Button onClick={save} data-testid="save-user-btn" className="w-full bg-[#1E3A8A] hover:bg-[#1e40af]">
              {editing ? "Güncelle" : "Kaydet"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
